import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Badge,
  IconButton,
  Select,
  Button,
  useDisclosure,
  Switch,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import {
  MdCheckCircle,
  MdEdit,
  MdOutlineError,
  MdPending,
  MdVisibility,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import LocationModal from './LocationModal';
import ErrorMessage from 'components/error-message';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function LocationTable(props) {
  const { tableData, loading, fetchAgain } = props;
  const navigate = useNavigate();
  const [type, setType] = React.useState('add');
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [selectedId, setSelectedId] = React.useState('');
  const [loadingStatus, setLoadingStatus] = React.useState({});

  const handleToggleStatus = async (locationId, currentStatus) => {
    setLoadingStatus((prev) => ({ ...prev, [locationId]: true }));
    try {
      const newStatus = !currentStatus; // Toggle the status
      const response = await createAPIEndPoint(
        `clinic_locations/`,
      ).update(locationId, { is_enable: newStatus });
      console.log('Status updated:', response.data);
      fetchAgain(); // Refresh the data
      toast.success('Status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update status.',
      );
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [locationId]: false }));
    }
  };

  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: 'id',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('location_name', {
      id: 'location_name',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          NAME
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('address', {
      id: 'address',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Address
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('state', {
      id: 'State',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          State
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('postal_code', {
      id: 'postal_code',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Postal Code
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row.is_enable ?? true, {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Status
        </Text>
      ),
      cell: (info) => {
        const isEnabled = info.row.original.is_enable ?? true; // Default to true if not provided
        return (
          <Tooltip
            label={isEnabled ? 'Enabled' : 'Disabled'}
            hasArrow
          >
            <span>
              {loadingStatus[info.row.original.id] ? (
                <Spinner size="md" mx="auto" display="flex" color="blue.500" />
              ) : (
                <Switch
                  isChecked={isEnabled}
                  onChange={() =>
                    handleToggleStatus(
                      info.row.original.id,
                      isEnabled,
                    )
                  }
                  colorScheme={isEnabled ? 'green' : 'red'}
                  isDisabled={loadingStatus[info.row.original.id]}
                />
              )}
            </span>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleViewClick = () => {
          const itemId = info.row.original.id; // Get the id from row data
          console.log('Navigating to details with ID:', itemId);

          navigate(`details/${itemId}`, { state: { id: itemId } }); // Pass ID in state
        };

        const handleEditClick = (e) => {
          const itemId = info.row.original.id; // Get the id from row data

          setSelectedId(itemId);
          onOpen();
          setType('edit');
        };

        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
            <IconButton
              icon={<MdVisibility style={{ marginLeft: '-1px' }} />}
              aria-label="View Details"
              colorScheme="blackAlpha"
              size="sm"
              onClick={handleViewClick}
            />
          </Box>
        );
      },
    }),
  ];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    if (tableData && tableData.length > 0) {
      setData([...tableData]);
    }
  }, [tableData]);

  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    if (!tableData) return;

    let filteredData = [...tableData]; // Ensure you work on a copy

    filteredData = filteredData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.location_name
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()) ||
        item.city
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()) ||
        item.email
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()) ||
        item.address
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase());

      return matchesSearch;
    });

    setCurrentPage(1);
    setData(filteredData);
  }, [tableData, searchTerm]);

  const itemsPerPageOptions = [10, 20, 30, 50];
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Text
            color={textColor}
            fontSize="24px"
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: '8px', md: '0px' }}
            textAlign={{ base: 'center', md: 'left' }}
          >
            Location{' '}
          </Text>

          <Box
            ml={{ base: 0, md: 'auto' }}
            mr={{ base: 0, md: 5 }}
            w={{ base: '100%', md: 'auto' }}
          >
            <SearchBar
              width="100%"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />{' '}
          </Box>

          <Box w={{ base: '100%', md: 'auto' }}>
            <Button
              w="100%"
              // minW={{ base: 'auto', md: '140px' }}
              variant="brand"
              fontWeight="500"
              onClick={() => {
                onOpen();
                setType('add');
              }}
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
            >
              Add Location
            </Button>
          </Box>
        </Flex>

        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          </Box>

          <Box
            w={{ base: '100%', md: 'auto' }}
            textAlign={{ base: 'center', md: 'right' }}
          >
            <Pagination
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />{' '}
          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {paginatedData.map((row, index) => (
                <Tr key={index}>
                  {table
                    .getRowModel()
                    .rows.find((r) => r.original === row) // Ensure correct row mapping
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        {!loading &&
          data.length === 0 &&
          (searchTerm.length > 0 ? (
            <ErrorMessage source="results" />
          ) : (
            <ErrorMessage source="locations" />
          ))}
      </Card>

      <LocationModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        locationId={selectedId}
        fetchAgain={fetchAgain}
      />
    </>
  );
}
